import React, { useEffect, useState, useMemo } from "react";
import { Helmet } from "react-helmet";
import { Table, Badge } from "react-bootstrap";
import { Link } from "react-router-dom";
import withAuth from "../../utils/withAuth";
import ContentWrapper from "../../components/contentwrapper/ContentWrapper";
import ReportNavBar from "../../components/ReportNavBar";
import { fetchAllDataAPI } from "../../utils/api";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const pct = (n) => `${Number(n || 0).toFixed(2)}%`;

const SkeletonRow = ({ cols = 4 }) => (
    <tr>
        {Array.from({ length: cols }).map((_, i) => (
            <td key={i}>
                <div className="skel-line" />
            </td>
        ))}
    </tr>
);

const ClusterHeadInOutPerformance = () => {
    const [rows, setRows] = useState([]);
    const [isLoading, setLoading] = useState(false);

    useEffect(() => {
        getClusterInOutPerformance();
    }, []);

    const getClusterInOutPerformance = async () => {
        try {
            setLoading(true);
            const res = await fetchAllDataAPI("super-admin-center-cluster-performance");
            if (res?.data?.status === 1 && Array.isArray(res.data.data)) {
                setRows(res.data.data);
            } else {
                setRows([]);
            }
        } catch (e) {
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    const totalSummary = useMemo(() => {
        if (!rows.length) return null;
        const sum = (arr, key) =>
            arr.reduce((a, r) => a + Number(r[key] || 0), 0);
        const inboundDelivered = sum(rows, "inbound_delivered");
        const inboundTotal = sum(rows, "inbound_total_orders");
        const outboundDelivered = sum(rows, "outbound_delivered");
        const outboundTotal = sum(rows, "outbound_total_orders");

        return {
            inbound_last15_amount: sum(rows, "inbound_last15_amount"),
            inbound_target_amount: sum(rows, "inbound_target_amount"),
            inbound_delivery_percent:
                inboundTotal > 0 ? ((inboundDelivered / inboundTotal) * 100) : 0,
            outbound_last15_amount: sum(rows, "outbound_last15_amount"),
            outbound_target_amount: sum(rows, "outbound_target_amount"),
            outbound_delivery_percent:
                outboundTotal > 0 ? ((outboundDelivered / outboundTotal) * 100) : 0,
        };
    }, [rows]);

    return (
        <div className="employeepage">
            <Helmet>
                <title>SATKARTAR::HR::CENTER PERFORMANCE</title>
            </Helmet>

            {/* Minimal CSS for skeleton shimmer */}
            <style>{`
        .skel-line {
          height: 14px;
          width: 100%;
          border-radius: 6px;
          background: linear-gradient(90deg, #eee 25%, #f5f5f5 37%, #eee 63%);
          background-size: 400% 100%;
          animation: skel 1.4s ease-in-out infinite;
        }
        @keyframes skel { 0% {background-position: 100% 0} 100% {background-position: 0 0} }
        .metric { display:flex; flex-direction:column; gap:10px; }
        .muted { font-size: 12px; opacity: 1; }
      `}</style>

            <ContentWrapper>
                <ReportNavBar />
                <div className="card employeepage" style={{marginBottom:"55px"}}>
                    <h5 className="mb-3 text-uppercase">Cluster Head Performance (Inbound / Outbound)</h5>
                    <div className="table pagetable table-responsive">
                        <Table className="table-sm align-middle" responsive striped bordered hover>
                            <thead>
                                <tr>
                                    <th style={{ width: 60 }}>#</th>
                                    <th>Cluster Head</th>
                                    <th style={{ minWidth: 300 }}>Inbound (Last 15 days & Delivery)</th>
                                    <th style={{ minWidth: 300 }}>Outbound (Last 15 days & Delivery)</th>
                                </tr>
                            </thead>

                            <tbody>
                                {isLoading ? (
                                    Array.from({ length: 8 }).map((_, idx) => (
                                        <SkeletonRow key={idx} cols={4} />
                                    ))
                                ) : rows.length ? (
                                    rows.map((r, i) => (
                                        <tr key={r.cluster_id || i}>
                                            <td>{i + 1}</td>
                                            <td>
                                                <div className="metric">
                                                    <div><Link to={`/center-head-in-out-performance-report/${r.cluster_id}`}>{r.cluster_name}</Link></div>
                                                </div>
                                            </td>

                                            {/* Inbound */}
                                            <td>
                                                <div className="metric">
                                                    <div>
                                                        <b>Sales 15d:</b> {money(r.inbound_last15_amount)}{" "}
                                                        <span className="muted">/ Target {money(r.inbound_target_amount)}</span>{" "}
                                                        {r.inbound_target_met ? (
                                                            <span className="targets">Target Met</span>
                                                        ) : (
                                                            <span className="targetm">Target Miss</span>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <b>Delivery 2m:</b> {pct(r.inbound_delivery_percent)}{" "}
                                                        <span className="muted">(goal ≥ 48%)</span>{" "}
                                                        {r.inbound_delivery_target_met ? (
                                                            <span className="targets">On Track</span>
                                                        ) : (
                                                            <span className="targetm">Below</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Outbound */}
                                            <td>
                                                <div className="metric">
                                                    <div>
                                                        <b>Sales 15d:</b> {money(r.outbound_last15_amount)}{" "}
                                                        <span className="muted">/ Target {money(r.outbound_target_amount)}</span>{" "}
                                                        {r.outbound_target_met ? (
                                                            <span className="targets">Target Met</span>
                                                        ) : (
                                                            <span className="targetm">Target Miss</span>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <b>Delivery 2m:</b> {pct(r.outbound_delivery_percent)}{" "}
                                                        <span className="muted">(goal ≥ 45%)</span>{" "}
                                                        {r.outbound_delivery_target_met ? (
                                                            <span className="targets">On Track</span>
                                                        ) : (
                                                            <span className="targetm">Below</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={4} className="text-center py-4">
                                            No data found
                                        </td>
                                    </tr>
                                )}
                            </tbody>

                            <tfoot>
                                <tr>
                                    <th colSpan={2}>Totals</th>
                                    <th>
                                        {totalSummary ? (
                                            <>
                                                <div><b>Sales 15d:</b> {money(totalSummary.inbound_last15_amount)} <span className="muted">/ Target {money(totalSummary.inbound_target_amount)}</span></div>
                                                <div><b>Delivery 2m:</b> {pct(totalSummary.inbound_delivery_percent)}</div>
                                            </>
                                        ) : (
                                            <div className="muted">—</div>
                                        )}
                                    </th>
                                    <th>
                                        {totalSummary ? (
                                            <>
                                                <div><b>Sales 15d:</b> {money(totalSummary.outbound_last15_amount)} <span className="muted">/ Target {money(totalSummary.outbound_target_amount)}</span></div>
                                                <div><b>Delivery 2m:</b> {pct(totalSummary.outbound_delivery_percent)}</div>
                                            </>
                                        ) : (
                                            <div className="muted">—</div>
                                        )}
                                    </th>
                                </tr>
                            </tfoot>
                        </Table>
                    </div>
                </div>
            </ContentWrapper>
        </div>
    );
};

export default withAuth(ClusterHeadInOutPerformance);
